<template>
  <aside
    v-if="ad && visible"
    class="ni-float-ad"
    :class="positionClass"
    :style="floatStyle"
    role="complementary"
    :aria-label="ad.title || '活动浮窗'"
  >
    <button
      v-if="ad.closeable !== false"
      type="button"
      class="ni-float-ad__close"
      aria-label="关闭"
      @click.stop="close"
    >
      ×
    </button>
    <a
      class="ni-float-ad__link"
      :href="href"
      :target="ad.target === '_blank' ? '_blank' : undefined"
      :rel="ad.target === '_blank' ? 'noopener noreferrer' : undefined"
      :title="ad.title"
    >
      <img class="ni-float-ad__img" :src="ad.image" :alt="ad.title || ''" />
    </a>
  </aside>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const STORAGE_PREFIX = 'ni_float_ad_closed_';

const props = defineProps({
  ad: {
    type: Object,
    default: null,
  },
});

const router = useRouter();
const visible = ref(true);
const floatTop = ref(0);
let scrollContainer = null;

const floatStyle = computed(() => (
  floatTop.value ? { top: `${floatTop.value}px` } : null
));

function updateFloatTop() {
  const hero = document.querySelector('.ni-hero');
  const bar = document.querySelector('.ni-hero__bar');
  if (!hero) {
    floatTop.value = 0;
    return;
  }
  const gap = 16;
  const barHeight = bar ? bar.offsetHeight : 80;
  const heroBottom = hero.getBoundingClientRect().bottom;
  floatTop.value = Math.max(barHeight + gap, heroBottom + gap);
}

function onScroll() {
  updateFloatTop();
}

function buildStorageKey(ad) {
  if (!ad || !ad.id) return '';
  const stamp = ad.updated_at || '';
  return `${STORAGE_PREFIX}${ad.id}_${stamp}`;
}

const positionClass = computed(() =>
  props.ad?.position === 'right' ? 'ni-float-ad--right' : 'ni-float-ad--left'
);

const href = computed(() => {
  const url = props.ad?.link_url || '';
  if (!url) return '#';
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) return url;
  try {
    return router.resolve(url).href;
  } catch {
    return url;
  }
});

function syncVisibility() {
  const ad = props.ad;
  if (!ad || !ad.image) {
    visible.value = false;
    return;
  }
  const key = buildStorageKey(ad);
  if (!key) {
    visible.value = true;
    return;
  }
  try {
    visible.value = !localStorage.getItem(key);
  } catch {
    visible.value = true;
  }
}

watch(
  () => props.ad,
  () => {
    syncVisibility();
  },
  { immediate: true }
);

onMounted(() => {
  scrollContainer = document.querySelector('.ni-page') || window;
  updateFloatTop();
  scrollContainer.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
});

onBeforeUnmount(() => {
  scrollContainer?.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
});

function close() {
  visible.value = false;
  const key = buildStorageKey(props.ad);
  if (!key) return;
  try {
    localStorage.setItem(key, '1');
  } catch {
    // ignore quota / private mode
  }
}
</script>

<style scoped>
/*
  与 .ni-container 对齐：容器 max-width 1200px 居中。
  左浮窗：右缘对齐容器左缘再留 gap；右浮窗对称。
*/
.ni-float-ad {
  --ni-float-w: 200px;
  --ni-float-gap: 24px;
  --ni-float-half: calc(var(--ni-container-max, 1200px) / 2);

  position: fixed;
  top: calc(32vw + 16px);
  z-index: 30;
  width: var(--ni-float-w);
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.14));
}

.ni-float-ad--left {
  left: max(
    12px,
    calc(50% - var(--ni-float-half) - var(--ni-float-w) - var(--ni-float-gap))
  );
  right: auto;
}

.ni-float-ad--right {
  right: max(
    12px,
    calc(50% - var(--ni-float-half) - var(--ni-float-w) - var(--ni-float-gap))
  );
  left: auto;
}

.ni-float-ad__close {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.ni-float-ad__close:hover {
  background: rgba(0, 0, 0, 0.85);
}

.ni-float-ad__link {
  display: block;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.ni-float-ad__img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: top;
}

/* 侧边留白收窄时略缩小，仍保持可见 */
@media (max-width: 1560px) {
  .ni-float-ad {
    --ni-float-w: 180px;
    --ni-float-gap: 16px;
  }
}

/* 移动端固定左下角，避免跟着轮播或脚本 top 跑位 */
@media (max-width: 767px) {
  .ni-float-ad,
  .ni-float-ad--left,
  .ni-float-ad--right {
    top: auto !important;
    right: auto !important;
    left: 12px !important;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    width: 150px;
    --ni-float-w: 150px;
  }
}
</style>
